/**
 * Highlights matched terms in text/markdown by wrapping them in styled spans.
 * Works with react-markdown + rehype-raw since they allow inline HTML.
 */

/**
 * Highlights terms in a markdown/text string by wrapping matches in styled spans.
 * Uses case-insensitive matching which works for both tickers and keywords.
 *
 * @param text - The text/markdown content to process
 * @param terms - Terms to highlight (tickers and/or keywords)
 * @param color - Background color for highlights
 * @returns The text with matched terms wrapped in styled spans
 */
export function highlightTermsInText(
  text: string | null | undefined,
  terms: string[],
  color: string
): string {
  if (!text || terms.length === 0) return text || "";

  const regex = createHighlightRegex(terms, true);
  return text.replace(
    regex,
    `<span style="background-color: ${color}; color: black; padding: 0 2px; border-radius: 2px;">$1</span>`
  );
}

/**
 * Creates a regex for word boundary matching with global flag for replacement.
 * Uses negative lookbehind/lookahead for proper word boundaries.
 */
function createHighlightRegex(
  terms: string[],
  caseInsensitive: boolean
): RegExp {
  const escapedTerms = terms.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const flags = caseInsensitive ? "gi" : "g";
  return new RegExp(
    `(?<![a-zA-Z0-9_])(${escapedTerms.join("|")})(?![a-zA-Z0-9_])`,
    flags
  );
}
